import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SingleResponseModel } from '../../modules_v2/common/models/single-response.model';
import { TaxFieldOriginModel } from './tax-field-origin/tax-field-origin.model';
import {
  Model184,
  Model184FieldOriginQuery,
  Model184Miembro,
  Model184Renta,
  Model184Validation,
  normalizarMiembro184,
  normalizarModelo184,
  normalizarRenta184
} from './model-184/taxes-184.model';

@Injectable({
  providedIn: 'root',
})
export class Model184Service {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${environment.apiUrl}/api/v2/tax-forms/184`;
  }

  public async calculate(year: number | string, receiptNumber?: string, kind?: string): Promise<Model184> {
    let params = new HttpParams().set('year', year.toString());

    if (receiptNumber) {
      params = params.set('receiptNumber', receiptNumber);

      if (kind) {
        params = params.set('kind', kind);
      }
    }

    const response = await firstValueFrom(
      this.http.get<SingleResponseModel<Model184>>(`${this.base}/calculate`, { params }));

    return normalizarModelo184(response.value);
  }

  public async recalculate(data: Model184): Promise<Model184> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model184>>(`${this.base}/recalculate`, data));

    return normalizarModelo184(response.value);
  }

  public async validate(data: Model184): Promise<Model184Validation[]> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model184Validation[]>>(`${this.base}/validate`, data));

    return response.value || [];
  }

  public async getFile(data: Model184): Promise<{ blob: Blob, nombre: string | null }> {
    const response = await firstValueFrom(
      this.http.post(`${this.base}/file`, data, { responseType: 'blob', observe: 'response' }));

    const cabecera = response.headers.get('Content-Disposition') || '';
    const coincidencia = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecera);
    const nombre = coincidencia ? decodeURIComponent(coincidencia[1].trim()) : null;

    return { blob: new Blob([response.body], { type: 'text/plain;charset=iso-8859-1' }), nombre };
  }

  public async importFile(contenido: string): Promise<Model184> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model184>>(`${this.base}/import`, { contenido }));

    return normalizarModelo184(response.value);
  }

  public async saveMember(taxYear: number, miembro: Model184Miembro): Promise<Model184Miembro> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model184Miembro>>(`${this.base}/members`, { taxYear, miembro }));

    return normalizarMiembro184(response.value);
  }

  public async deleteMember(uid: string): Promise<void> {
    await firstValueFrom(this.http.delete<SingleResponseModel<boolean>>(`${this.base}/members/${uid}`));
  }

  public async saveRent(taxYear: number, renta: Model184Renta): Promise<Model184Renta> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model184Renta>>(`${this.base}/rents`, { taxYear, renta }));

    return normalizarRenta184(response.value);
  }

  public async deleteRent(uid: string): Promise<void> {
    await firstValueFrom(this.http.delete<SingleResponseModel<boolean>>(`${this.base}/rents/${uid}`));
  }

  public async getOrigen(taxYear: number, renta: Model184Renta, campo: string): Promise<TaxFieldOriginModel> {
    const query: Model184FieldOriginQuery = {
      campo,
      taxYear,
      clave: renta.clave,
      subclave: renta.subclave,
      valorEnElFormulario: Number((renta as any)[campo]) || 0
    };

    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<TaxFieldOriginModel>>(`${this.base}/field-origin`, query));

    return response.value;
  }
}
